/**
 * ValueList-backed attributes on the ComplianceClause (obligation) model.
 *
 * The read key and the write key differ for every one of these: the API returns
 * `<field>` as a nested ValueList object (or an array of them) and expects
 * `writeKey` on POST/PATCH, holding each option's `valueKey` - the pk for the
 * single-valued attributes, the option name for `regulated_entity_category`
 * (stored as an array of names so it matches the compliance store).
 *
 * `field` doubles as the filter query param and the reactive-form control name.
 * Filters always send option ids, whatever the `valueKey`.
 */
export interface ObligationAttributeCfg {
  field: string;
  writeKey: string;
  label: string;
  valueListType: string;
  multiple?: boolean;
  /** The option property the form holds and the API is sent. Defaults to "id". */
  valueKey?: "id" | "name";
}

export const OBLIGATION_VALUE_LIST_ATTRIBUTES: ObligationAttributeCfg[] = [
  {
    field: "category",
    writeKey: "category_id",
    label: "Category",
    valueListType: "compliance_obligation_category",
  },
  {
    field: "sub_category",
    writeKey: "sub_category_id",
    label: "Sub Category",
    // Shares the category list — the serializer validates both against this type.
    valueListType: "compliance_obligation_sub_category",
  },
  {
    field: "subject",
    writeKey: "subject_id",
    label: "Subject",
    valueListType: "compliance_obligation_subject",
  },
  {
    field: "jurisdiction_level",
    writeKey: "jurisdiction_level_id",
    label: "Jurisdiction Level",
    valueListType: "compliance_obligation_jurisdiction",
  },
  {
    field: "requirement_type",
    writeKey: "requirement_type_id",
    label: "Requirement Type",
    valueListType: "compliance_obligation_requirement_type",
  },
  {
    field: "mandatory_optional",
    writeKey: "mandatory_optional_id",
    label: "Mandatory / Optional",
    valueListType: "compliance_necessity_option",
  },
  {
    field: "obligation_type",
    writeKey: "obligation_type_id",
    label: "Obligation Type",
    valueListType: "compliance_obligation_type",
  },
  {
    field: "risk_severity",
    writeKey: "risk_severity_id",
    label: "Risk Severity",
    valueListType: "compliance_risk_severity",
  },
  {
    field: "penalty_type",
    writeKey: "penalty_type_id",
    label: "Penalty Type",
    valueListType: "compliance_penalty_type",
  },
  {
    field: "evidence_sufficiency",
    writeKey: "evidence_sufficiency_id",
    label: "Evidence Sufficiency",
    valueListType: "compliance_evidence_sufficiency_option",
  },
  {
    field: "regulated_entity_category",
    writeKey: "regulated_entity_category_names",
    label: "Regulated Entity Category",
    valueListType: "compliance_regulated_entity",
    multiple: true,
    valueKey: "name",
  },
];

/** The option property an attribute's form control holds. */
export function attributeValueKey(attr: ObligationAttributeCfg): "id" | "name" {
  return attr.valueKey ?? "id";
}

export const DOCUMENT_TYPE_FIELD = "document_type";
export const DOCUMENT_TYPE_LABEL = "Document Type";
export const DOCUMENT_TYPE_VALUE_LIST_TYPE = "compliance_doc_type";

/**
 * Every filter pill on an obligation/document list whose value is a list of
 * ValueList objects and whose query param is a comma-separated list of ids.
 */
export const VALUE_LIST_FILTER_FIELDS: string[] = [
  DOCUMENT_TYPE_FIELD,
  ...OBLIGATION_VALUE_LIST_ATTRIBUTES.map((attr) => attr.field),
];

/** Filter-pill configs for the obligation ValueList attributes. */
export function buildObligationAttributeFilterInputs(options: {
  [field: string]: any[];
}): any[] {
  return OBLIGATION_VALUE_LIST_ATTRIBUTES.map((attr) => ({
    field: attr.field,
    type: "searchable-multiselect",
    placeholder: `Search ${attr.label}...`,
    value: [],
    options: options?.[attr.field] || [],
  }));
}

/**
 * Pulls the `VALUE_LIST_FILTER_FIELDS` pills out of a filter-input list so the
 * generic `key=value` serialisation does not stringify their object arrays,
 * and returns the `&field=id,id` fragments for them.
 */
export function extractValueListFilterParams(inputs: any[]): {
  remaining: any[];
  params: string;
} {
  let remaining = inputs;
  let params = "";

  VALUE_LIST_FILTER_FIELDS.forEach((field) => {
    const filter = remaining.find((input: any) => input?.field === field);
    if (!filter?.value?.length) return;

    remaining = remaining.filter((input: any) => input?.field !== field);
    const ids = filter.value
      .map((item: any) => item?.id ?? item?.name ?? item)
      .join(",");
    params += `&${field}=${encodeURIComponent(ids)}`;
  });

  return { remaining, params };
}
